import axios from 'axios';
import type {
  RecentEventsResponse,
  RecentEventsCondition,
  FilterCatalogResponse,
  BufferClearedResponse,
} from '../types/eventMonitor';
import { getStoredToken } from '../../login/api/loginApi';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
attachAuthInterceptors(apiClient);

// Request interceptor — attach Bearer token
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — unwrap ApiResponse<T>
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: err?.['code'] ?? `HTTP-${response.status}`,
        message: err?.['message'] ?? 'Unknown error',
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
      });
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const code = errData?.['code'] ?? `HTTP-${error.response?.status ?? 0}`;
    const message =
      (errData?.['message'] as string) ??
      (error.response?.data?.message as string) ??
      error.message ??
      'Network error';
    const traceId = (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    return Promise.reject({ code, message, traceId });
  }
);

function buildConditionParams(condition: RecentEventsCondition): URLSearchParams {
  const sp = new URLSearchParams();
  if (condition.since) sp.set('since', condition.since);
  if (condition.type) sp.set('type', condition.type);
  if (condition.source) sp.set('source', condition.source);
  if (condition.userId) sp.set('userId', condition.userId);
  if (condition.limit !== undefined) sp.set('limit', String(condition.limit));
  return sp;
}

export const eventMonitorApi = {
  /**
   * GET /api/eraf/event-monitor/recent
   * 최근 이벤트 목록 (ROLE_ADMIN)
   */
  getRecent: (condition: RecentEventsCondition = {}): Promise<RecentEventsResponse> => {
    const sp = buildConditionParams(condition);
    const query = sp.toString();
    return apiClient
      .get<unknown, { data: RecentEventsResponse }>(`/event-monitor/recent${query ? `?${query}` : ''}`)
      .then((res) => res.data);
  },

  /**
   * GET /api/eraf/event-monitor/filters
   * 필터 카탈로그 — buffer 내 distinct type/source (ROLE_ADMIN)
   */
  getFilters: (): Promise<FilterCatalogResponse> =>
    apiClient
      .get<unknown, { data: FilterCatalogResponse }>('/event-monitor/filters')
      .then((res) => res.data),

  /**
   * DELETE /api/eraf/event-monitor/buffer
   * Buffer 전체 클리어 (ROLE_ADMIN)
   */
  clearBuffer: (): Promise<BufferClearedResponse> =>
    apiClient
      .delete<unknown, { data: BufferClearedResponse }>('/event-monitor/buffer')
      .then((res) => res.data),

  /**
   * POST /api/eraf/event-monitor/ticket
   * SSE 인증용 단명 ticket 발급 (R1-004) — 60s · 1회용
   */
  issueTicket: (): Promise<string> =>
    apiClient
      .post<unknown, { data: { ticket: string } }>('/event-monitor/ticket')
      .then((res) => res.data.ticket),

  /**
   * SSE 연결용 URL 반환 (ticket 포함). EventSource 는 hook 에서 직접 생성.
   * 토큰 평문 노출 X — 1회용 60s ticket 만 query param 으로 전달.
   */
  getStreamUrl: (ticket: string, typeFilter?: string): string => {
    const base = import.meta.env.VITE_API_BASE ?? '/api/eraf';
    const sp = new URLSearchParams();
    sp.set('ticket', ticket);
    if (typeFilter) sp.set('type', typeFilter);
    return `${base}/event-monitor/stream?${sp.toString()}`;
  },
};
